import React, { Component } from 'react';
import { LoadScript } from '../../types/LoadScript';
import { ScriptLoader } from '../../helpers/script-loader'
import './loadable-component.scss'
import 'placeholder-loading/dist/css/placeholder-loading.min.css'

/**
 * A component that makes a load operation in order to be displayed (such as a web component).
 * While it's being loaded, a placeholder element can be shown in its place until the load finishes.
 */

type Props = {
    scriptsToBeLoaded: LoadScript[],
    onErrorCallback?: () => void,
    onLoadCallback?: () => void,
    container?: React.ElementType,
    errorPlaceholder: React.ElementType,
    loadPlaceholder: React.ElementType,
    message: JSX.Element
    hasOutsideApiError?: boolean;
}

type State = {
    loadRequested: boolean,
    isLoading: boolean,
    hasError: boolean,
    scriptsInfo: LoadScript[],
    scriptLoaders: any
}

class LoadableComponent extends Component<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = { loadRequested: false, isLoading: true, hasError: false, scriptsInfo: [], scriptLoaders: [] };

        this._loadScripts = this._loadScripts.bind(this);
        this._onLoad = this._onLoad.bind(this);
        this._onError = this._onError.bind(this);
        this._withContainer = this._withContainer.bind(this);
        this._renderError = this._renderError.bind(this);
        this._renderLoading = this._renderLoading.bind(this);
    }

    async componentDidMount() {
        this.setState({ scriptsInfo: this.props.scriptsToBeLoaded }, () => {
            this._loadScripts(false);
        });

        this.handleOutsideApiError();
    }

    handleOutsideApiError() {
        if(this.props.hasOutsideApiError){
            this._onError()
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.scriptsToBeLoaded !== this.props.scriptsToBeLoaded) {
            this.setState({ scriptsInfo: this.props.scriptsToBeLoaded }, () => {
                this._loadScripts(false);
            });
        }
    }

    _loadScripts(useTimespan: boolean) {
        const scriptsInfo = this.state.scriptsInfo;
        if (scriptsInfo && scriptsInfo.length > 0) {
            const scriptLoadersAux: any = [];

            scriptsInfo.forEach((scriptInfo: LoadScript) => {
                if (scriptInfo.url) {
                    let scriptLoader = this.state.scriptLoaders[scriptInfo.url];
                    if (!scriptLoader) {
                        this.state.scriptLoaders[scriptInfo.url] = scriptLoader = new ScriptLoader(scriptInfo);
                    }
                    scriptLoadersAux.push(scriptLoader);
                }
            });

            this.setState({ isLoading: true, hasError: false }, () => {
                scriptLoadersAux.forEach((scriptLoader: ScriptLoader) => {
                    scriptLoader.load(this._onLoad, this._onError, useTimespan);
                });
            })
        }
    }

    _loadScriptsWithTimespan() {
        return this._loadScripts(true);
    }

    _onLoad() {
        this.setState({ isLoading: false, hasError: false });
        if (this.props.onLoadCallback)
            this.props.onLoadCallback();
    }

    _onError() {
        this.setState({ isLoading: false, hasError: true });
        if (this.props.onErrorCallback)
            this.props.onErrorCallback();
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    /**
     * Adds an external container (if it exists) to the element, usally for styling it.
     * @param {*} element Element to be encapsulated by the container.
     * @returns Final element.
     */
    _withContainer(element: JSX.Element) {
        const Container = this.props.container;
        if (Container)
            return <Container>{element}</Container>;
        else
            return element;
    }

    /**
     * Renders the error placeholder whenever an error occurs.
     * @returns Error placeholder.
     */
    _renderError() {
        const ErrorPlaceholder = this.props.errorPlaceholder;
        const element =

            <div className='loadable-component-container'>
                <ErrorPlaceholder onReloadClick={this._loadScriptsWithTimespan.bind(this)} message={this.props.message} />
            </div>

        return this._withContainer(element);
    }

    _renderLoading() {
        // If the component is still loading, include the load placeholder element.
        const LoadPlaceholder = this.props.loadPlaceholder;
        const element =

            <div className='loadable-component-container'>
                <LoadPlaceholder />
            </div>

        return this._withContainer(element);
    }

    render() {
        let placeholder;
        if (this.state.hasError)
            placeholder = this._renderError();
        else if (this.state.isLoading)
            placeholder = this._renderLoading();

        return (
            <div>
                { placeholder }
                {this.props.children}
            </div>
        );
    }
}

export default LoadableComponent;